import React, { useState } from 'react';
import { PlayIcon, XIcon, ChevronLeftIcon, ChevronRightIcon, ListIcon, CheckIcon, SparklesIcon, MonitorIcon, RotateCcwIcon } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
export type DemoTab = 'gerer' | 'analytiques' | 'configurer';
export type DemoSection = 'inscriptions' | 'camps' | 'reservations' | 'equipements' | 'acces';
export type DemoPanel = 'ressources' | 'utilisation' | 'contrats' | 'paiements' | 'prealables' | 'sessions' | 'organismes' | 'acces' | 'documents';
export interface DemoTarget {
  tab: DemoTab;
  section?: DemoSection;
  panel?: DemoPanel;
}
interface Step {
  /** Short label shown in the step list and the progress rail. */
  label: {
    fr: string;
    en: string;
  };
  /** One-line pitch the presenter can say out loud. */
  pitch: {
    fr: string;
    en: string;
  };
  /** Concrete things to click or point at on screen. */
  points: {
    fr: string;
    en: string;
  }[];
  target: DemoTarget;
}
const STEPS: Step[] = [{
  label: {
    fr: 'Le catalogue',
    en: 'The catalogue'
  },
  pitch: {
    fr: 'Toutes les activités de la saison dans un seul tableau : horaires multiples, jauge de remplissage, tarifs résident et non-résident, visibilité au catalogue citoyen.',
    en: 'Every activity of the season in a single table: multiple schedules, fill gauge, resident and non-resident rates, visibility in the citizen catalogue.'
  },
  points: [{
    fr: 'Natation — Junior se tient le lundi 16 h et le jeudi 18 h : une activité, deux créneaux.',
    en: 'Swimming — Junior runs Monday 4 pm and Thursday 6 pm: one activity, two slots.'
  }, {
    fr: 'La jauge passe à l’ambre à 80 % et au rouge à pleine capacité, avec la liste d’attente à droite.',
    en: 'The gauge turns amber at 80% and red at capacity, with the waitlist on the right.'
  }, {
    fr: 'L’interrupteur « Visible » retire l’activité du catalogue sans l’ouvrir.',
    en: 'The “Visible” switch removes the activity from the catalogue without opening it.'
  }],
  target: {
    tab: 'gerer',
    section: 'inscriptions'
  }
}, {
  label: {
    fr: 'Créer une activité',
    en: 'Create an activity'
  },
  pitch: {
    fr: 'Cliquez sur « Nouvelle activité » : un seul formulaire, du type jusqu’aux ressources, avec détection de conflits en direct.',
    en: 'Click “New activity”: a single form, from the discipline to the resources, with live conflict detection.'
  },
  points: [{
    fr: 'Ajoutez deux créneaux hebdomadaires — le formulaire signale si le plateau est déjà occupé.',
    en: 'Add two weekly slots — the form flags whether the facility is already busy.'
  }, {
    fr: 'Dans « Ressources nécessaires », cochez un professeur, un sauveteur et du matériel : la disponibilité est vérifiée sur chaque créneau.',
    en: 'In “Required resources”, tick a teacher, a lifeguard and equipment: availability is checked on every slot.'
  }, {
    fr: 'Ajoutez un préalable bloquant, puis décidez de la visibilité au catalogue.',
    en: 'Add a blocking prerequisite, then decide on catalogue visibility.'
  }],
  target: {
    tab: 'gerer',
    section: 'inscriptions'
  }
}, {
  label: {
    fr: 'Faire l’appel',
    en: 'Call the roll'
  },
  pitch: {
    fr: 'Ouvrez une activité, puis « Feuille de présences » : l’instructeur pointe son groupe depuis une tablette, au bord du plateau.',
    en: 'Open an activity, then “Attendance sheet”: the instructor calls the roll from a tablet at the venue.'
  },
  points: [{
    fr: 'Présent / Retard / Absent en un geste, ou « Tout marquer présent » pour ne pointer que les exceptions.',
    en: 'Present / Late / Absent in one tap, or “Mark all present” to record only the exceptions.'
  }, {
    fr: 'La liste des présents se construit à droite, imprimable pour le plateau.',
    en: 'The present list builds up on the right, printable for the venue.'
  }, {
    fr: 'L’historique des séances montre le taux de présence et permet de rattraper un appel oublié.',
    en: 'The session history shows attendance rates and lets you catch up on a missed roll call.'
  }],
  target: {
    tab: 'gerer',
    section: 'inscriptions'
  }
}, {
  label: {
    fr: 'Camps de jour',
    en: 'Day camps'
  },
  pitch: {
    fr: 'Le suivi dossier par dossier : fiche santé, relevé 24, paiement et alertes de sécurité au même endroit.',
    en: 'File-by-file tracking: health form, Relevé 24, payment and safety alerts in one place.'
  },
  points: [{
    fr: 'Les fiches santé incomplètes ou manquantes sont signalées avant le premier jour.',
    en: 'Incomplete or missing health forms are flagged before day one.'
  }, {
    fr: 'Le relevé 24 se suit par enfant : émis, à émettre, ou non admissible.',
    en: 'The Relevé 24 is tracked per child: issued, to issue, or not eligible.'
  }, {
    fr: 'Les alertes remontent le concret : allergie avec ÉpiPen, autorisation de baignade non signée.',
    en: 'Alerts surface the concrete: allergy with EpiPen, unsigned swimming authorization.'
  }],
  target: {
    tab: 'gerer',
    section: 'camps'
  }
}, {
  label: {
    fr: 'Le calendrier',
    en: 'The calendar'
  },
  pitch: {
    fr: 'Le cœur du module : toutes les ressources sur un calendrier lisible, où les disponibilités se lisent en négatif.',
    en: 'The heart of the module: every resource on a readable calendar, where availability reads in the negative.'
  },
  points: [{
    fr: 'Basculez Mois / Semaine / Jour, puis filtrez par bâtiment — cocher un parent coche ses salles.',
    en: 'Switch Month / Week / Day, then filter by building — ticking a parent ticks its rooms.'
  }, {
    fr: 'Chaque couleur est un type d’utilisation : hockey mineur, patinage artistique, location de salle.',
    en: 'Each colour is a usage type: minor hockey, figure skating, room rental.'
  }, {
    fr: 'L’arrosage de la glace apparaît en bandeau fin, pas en gros bloc : la journée reste lisible.',
    en: 'Ice resurfacing appears as a thin band, not a large block: the day stays readable.'
  }],
  target: {
    tab: 'gerer',
    section: 'reservations'
  }
}, {
  label: {
    fr: 'Dossier et contrat',
    en: 'File and contract'
  },
  pitch: {
    fr: 'Cliquez un événement : une réservation est un dossier complet, pas une ligne — et le contrat se signe depuis la plateforme.',
    en: 'Click an event: a booking is a complete file, not a row — and the contract is signed from the platform.'
  },
  points: [{
    fr: 'Onglet Contrat : la timeline Généré → Envoyé → Signé, avec relance en un clic.',
    en: 'Contract tab: the Generated → Sent → Signed timeline, with a one-click reminder.'
  }, {
    fr: 'Une entente rédigée par le greffe s’importe au dossier ; une location scolaire produit un contrat à 0 $.',
    en: 'An agreement drafted by the clerk is imported into the file; a school rental issues a $0 contract.'
  }, {
    fr: 'Sous-onglet Contrats : la file des contrats à générer, en attente de signature et en retard.',
    en: 'Contracts sub-tab: the queue of contracts to generate, awaiting signature and overdue.'
  }],
  target: {
    tab: 'gerer',
    section: 'reservations'
  }
}, {
  label: {
    fr: 'Contrôle d’accès',
    en: 'Access control'
  },
  pitch: {
    fr: 'Au complexe aquatique, la carte citoyenne ouvre le tourniquet — et le refus est expliqué.',
    en: 'At the aquatic complex, the citizen card opens the turnstile — and refusals are explained.'
  },
  points: [{
    fr: 'Occupation en temps réel contre le maximum sécuritaire, par point d’accès.',
    en: 'Real-time occupancy against the safe maximum, per access point.'
  }, {
    fr: 'Le journal donne le motif de chaque refus : droit expiré, non-résident, carte inconnue.',
    en: 'The log gives the reason for each refusal: expired entitlement, non-resident, unknown card.'
  }],
  target: {
    tab: 'gerer',
    section: 'acces'
  }
}, {
  label: {
    fr: 'Analytiques',
    en: 'Analytics'
  },
  pitch: {
    fr: 'Le remplissage, l’achalandage et le rendement des plateaux, avec les recommandations ATNA.',
    en: 'Fill rates, attendance and facility performance, with ATNA recommendations.'
  },
  points: [{
    fr: 'ATNA propose d’ouvrir un second groupe de natation et de relancer les fiches santé manquantes.',
    en: 'ATNA suggests opening a second swimming group and following up on missing health forms.'
  }, {
    fr: 'Le planning de ressource montre semaine par semaine les heures encore réservables.',
    en: 'The resource schedule shows, week by week, the hours still bookable.'
  }],
  target: {
    tab: 'analytiques'
  }
}, {
  label: {
    fr: 'Virement Interac',
    en: 'Interac transfer'
  },
  pitch: {
    fr: 'La demande explicite de la Ville : payer par virement Interac, sans carte de crédit.',
    en: 'The City’s explicit request: pay by Interac transfer, with no credit card.'
  },
  points: [{
    fr: 'Le parcours citoyen en trois étapes : demande de fonds, application bancaire, place confirmée.',
    en: 'The three-step citizen journey: request for money, banking app, spot confirmed.'
  }, {
    fr: 'Le délai avant libération automatique de la place et le mode de réconciliation se configurent ici.',
    en: 'The delay before the spot is released and the reconciliation mode are configured here.'
  }, {
    fr: 'Les virements en attente se réconcilient depuis la file « Virements Interac à réconcilier ».',
    en: 'Pending transfers are reconciled from the “Interac transfers to reconcile” queue.'
  }],
  target: {
    tab: 'configurer',
    panel: 'paiements'
  }
}, {
  label: {
    fr: 'Configuration',
    en: 'Configuration'
  },
  pitch: {
    fr: 'Tout est paramétrable sans développeur : ressources, types d’utilisation, modèles de contrats.',
    en: 'Everything is configurable without a developer: resources, usage types, contract templates.'
  },
  points: [{
    fr: 'L’arborescence des ressources reflète les bâtiments réels et leurs salles.',
    en: 'The resource tree mirrors the real buildings and their rooms.'
  }, {
    fr: 'Un type d’utilisation pilote sa couleur, son contrat par défaut, sa tarification et sa visibilité publique.',
    en: 'A usage type drives its colour, default contract, pricing and public visibility.'
  }],
  target: {
    tab: 'configurer',
    panel: 'ressources'
  }
}];
interface Props {
  onNavigate: (target: DemoTarget) => void;
}
export function DemoBanner({
  onNavigate
}: Props) {
  const {
    t,
    lang
  } = useLanguage();
  const [running, setRunning] = useState(false);
  const [index, setIndex] = useState(0);
  const [listOpen, setListOpen] = useState(false);
  const [done, setDone] = useState<number[]>([]);
  const step = STEPS[index];
  const goTo = (next: number) => {
    const safe = Math.min(Math.max(next, 0), STEPS.length - 1);
    setIndex(safe);
    setDone(prev => prev.includes(index) ? prev : [...prev, index]);
    onNavigate(STEPS[safe].target);
  };
  const start = () => {
    setRunning(true);
    setIndex(0);
    setDone([]);
    onNavigate(STEPS[0].target);
  };

  // --------------------------------------------------------------- COLLAPSED
  if (!running) {
    return <div className="mb-6 rounded-xl border border-indigo-100 bg-indigo-50/60 px-5 py-4" data-id="element-3120" data-mp="19cr3qgf4kz94">
        <div className="flex items-center justify-between gap-4 flex-wrap" data-id="element-3121" data-mp="vd7x5s1ypvyx">
          <div className="flex items-start gap-3 min-w-0" data-id="element-3122" data-mp="10culawjbwwmi">
            <span className="w-9 h-9 rounded-lg bg-white border border-indigo-100 text-[#4F46E5] flex items-center justify-center shrink-0" data-id="element-3123" data-mp="10cb3dftoi88h">
              <MonitorIcon className="w-4 h-4" data-id="element-3124" data-mp="2xbbg1i83lrbh" />
            </span>
            <div className="min-w-0" data-id="element-3125" data-mp="2xa09jan9q5go">
              <p className="text-sm font-bold text-[#0F172A] flex items-center gap-2 flex-wrap" data-id="element-3126" data-mp="2t9gdybicf9wn">
                {t('Démo Loisirs', 'Recreation demo')}
                <span className="px-2 py-0.5 rounded-full bg-white text-[#4F46E5] text-xs font-semibold border border-indigo-100" data-id="element-3127" data-mp="2oc6hibwjdxzb">
                  {STEPS.length} {t('étapes', 'steps')}
                </span>
              </p>
              <p className="text-sm text-[#3730A3] mt-0.5" data-id="element-3128" data-mp="3gxg4pbfnn0b8">
                {t('Un parcours guidé, de la création d’activité au virement Interac. Chaque étape vous amène au bon écran avec le discours à tenir.', 'A guided walkthrough, from creating an activity to Interac transfers. Each step takes you to the right screen with the talking points.')}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 shrink-0" data-id="element-3129" data-mp="1ljc1ox8erahb">
            <button onClick={() => setListOpen(v => !v)} className="inline-flex items-center gap-2 px-3.5 py-2 text-sm font-medium text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors" data-id="element-3130" data-mp="1qgucyu9we1sh">
              <ListIcon className="w-4 h-4" data-id="element-3131" data-mp="20901fu0xy12x" />
              {t('Voir le scénario', 'See the script')}
            </button>
            <button onClick={start} className="inline-flex items-center gap-2 px-4 py-2 bg-[#4F46E5] hover:bg-[#4338CA] text-white text-sm font-semibold rounded-lg transition-colors shadow-sm" data-id="element-3132" data-mp="297bs1w1t4yvd">
              <PlayIcon className="w-4 h-4" data-id="element-3133" data-mp="2j4q4zloiv5ix" />
              {t('Lancer la démo', 'Start the demo')}
            </button>
          </div>
        </div>

        {listOpen && <ol className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 mt-4 pt-4 border-t border-indigo-100" data-id="element-3134" data-mp="or8fvu8gy0ls">
            {STEPS.map((item, i) => <li key={item.label.fr} data-id="element-3135" data-mp="2faym0tooxv2l" data-mpi={i}>
                <button onClick={() => {
            setRunning(true);
            setIndex(i);
            onNavigate(item.target);
          }} className="w-full flex items-center gap-2.5 rounded-lg bg-white border border-indigo-100 px-3 py-2 text-left hover:border-[#4F46E5] transition-colors" data-id="element-3136" data-mp="2aaajbar3npsj">
                  <span className="w-5 h-5 rounded-full bg-indigo-50 text-[#4F46E5] text-[11px] font-bold flex items-center justify-center shrink-0 tabular-nums" data-id="element-3137" data-mp="1rj9p100xcifb">
                    {i + 1}
                  </span>
                  <span className="text-sm font-medium text-slate-700 truncate" data-id="element-3138" data-mp="20jfxg7hrms7m">
                    {item.label[lang]}
                  </span>
                </button>
              </li>)}
          </ol>}
      </div>;
  }

  // ----------------------------------------------------------------- RUNNING
  return <div className="mb-6 rounded-xl border-2 border-[#4F46E5]/30 bg-white overflow-hidden" data-id="element-3139" data-mp="3jhucndlp95ki">
      {/* Rail */}
      <div className="flex items-center gap-2 px-5 py-3 bg-[#0F172A] flex-wrap" data-id="element-3140" data-mp="3oanffvxly3ty">
        <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-white/10 text-white text-xs font-semibold" data-id="element-3141" data-mp="3aj8uvko1xuks">
          <SparklesIcon className="w-3.5 h-3.5 text-[#A5B4FC]" data-id="element-3142" data-mp="3fey0a4mr65p0" />
          {t('Démo Loisirs', 'Recreation demo')}
        </span>
        <ol className="flex items-center gap-1 flex-1 min-w-0 overflow-x-auto" data-id="element-3143" data-mp="b2kbc7pmw4ds">
          {STEPS.map((item, i) => {
          const current = i === index;
          return <li key={item.label.fr} data-id="element-3144" data-mp="3tat91hnfu0eo" data-mpi={i}>
                <button onClick={() => goTo(i)} aria-current={current ? 'step' : undefined} aria-label={`${i + 1}. ${item.label[lang]}`} title={item.label[lang]} className={`h-1.5 rounded-full transition-all ${current ? 'w-8 bg-[#A5B4FC]' : done.includes(i) ? 'w-4 bg-white/50 hover:bg-white/80' : 'w-4 bg-white/20 hover:bg-white/40'}`} data-id="element-3145" data-mp="21coclskipz0" />
              </li>;
        })}
        </ol>
        <span className="text-xs text-slate-400 tabular-nums shrink-0" data-id="element-3146" data-mp="1oylosjc8nzyr">
          {index + 1} / {STEPS.length}
        </span>
        <button onClick={() => {
        setIndex(0);
        setDone([]);
        onNavigate(STEPS[0].target);
      }} aria-label={t('Recommencer la démo', 'Restart the demo')} title={t('Recommencer', 'Restart')} className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-white/10 transition-colors shrink-0" data-id="element-3147" data-mp="1jxejcqxkjcv9">
          <RotateCcwIcon className="w-4 h-4" data-id="element-3148" data-mp="35b12cryxbcz8" />
        </button>
        <button onClick={() => setRunning(false)} aria-label={t('Quitter la démo', 'Exit the demo')} title={t('Quitter la démo', 'Exit the demo')} className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-white/10 transition-colors shrink-0" data-id="element-3149" data-mp="30f2a2tah5atj">
          <XIcon className="w-4 h-4" data-id="element-3150" data-mp="3hoo1a6zeuol1" />
        </button>
      </div>

      {/* Current step */}
      <div className="px-5 py-4" data-id="element-3151" data-mp="3mlefspvkhc4c">
        <div className="flex items-start justify-between gap-4 flex-wrap" data-id="element-3152" data-mp="3moiq84zml43g">
          <div className="min-w-0 flex-1" data-id="element-3153" data-mp="2p08nfns0mcba">
            <p className="text-xs font-bold text-[#4F46E5] uppercase tracking-wider" data-id="element-3154" data-mp="2txr09p95lbok">
              {t('Étape ', 'Step ')}
              {index + 1}
            </p>
            <h3 className="text-lg font-bold text-[#0F172A] font-heading mt-0.5" data-id="element-3155" data-mp="2yvk3c9f5tpkm">
              {step.label[lang]}
            </h3>
            <p className="text-sm text-slate-600 mt-1 max-w-3xl" data-id="element-3156" data-mp="e6ml9ajsi7js">
              {step.pitch[lang]}
            </p>
            <ul className="space-y-1.5 mt-3" data-id="element-3157" data-mp="i7ooe2gtv3r1">
              {step.points.map(point => <li key={point.fr} className="flex items-start gap-2 text-sm text-slate-600" data-id="element-3158" data-mp="149i5blqb1bvi">
                  <CheckIcon className="w-4 h-4 mt-0.5 text-[#4F46E5] flex-shrink-0" data-id="element-3159" data-mp="zc88hl0l5xxn" />
                  {point[lang]}
                </li>)}
            </ul>
          </div>

          <div className="flex items-center gap-2 shrink-0" data-id="element-3160" data-mp="1982vesbuef68">
            <button onClick={() => goTo(index - 1)} disabled={index === 0} className="inline-flex items-center gap-2 px-3.5 py-2 text-sm font-medium text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed" data-id="element-3161" data-mp="1mzzlyrxv4h0i">
              <ChevronLeftIcon className="w-4 h-4" data-id="element-3162" data-mp="1rzvo0ln01ydf" />
              {t('Précédent', 'Back')}
            </button>
            {index === STEPS.length - 1 ? <button onClick={() => setRunning(false)} className="inline-flex items-center gap-2 px-4 py-2 bg-[#4F46E5] hover:bg-[#4338CA] text-white text-sm font-semibold rounded-lg transition-colors shadow-sm" data-id="element-3163" data-mp="3tr8qz55epw6u">
                <CheckIcon className="w-4 h-4" data-id="element-3164" data-mp="2e9ua5lom7k8g" />
                {t('Terminer la démo', 'Finish the demo')}
              </button> : <button onClick={() => goTo(index + 1)} className="inline-flex items-center gap-2 px-4 py-2 bg-[#4F46E5] hover:bg-[#4338CA] text-white text-sm font-semibold rounded-lg transition-colors shadow-sm" data-id="element-3165" data-mp="24h6ggzsxlwdy">
                {t('Étape suivante', 'Next step')}
                <ChevronRightIcon className="w-4 h-4" data-id="element-3166" data-mp="1gl0z6fh5pifg" />
              </button>}
          </div>
        </div>
      </div>
    </div>;
}